/**
 * [INPUT]: 依赖 react-router 的 MetaDescriptor/LinkDescriptor 类型；依赖 ./site 的 SITE/absoluteUrl/RouteSeo
 * [OUTPUT]: 对外提供 buildMeta / buildLinks / stripSiteSuffix —— 由 RR7 各 route 的 meta/links export 调用，
 *   在构建期预渲染与客户端导航统一注入路由级 head（title/description/robots/canonical/og 与 twitter 系列）
 * [POS]: apps/web/seo 的构建期 head 真源；取代旧 Seo.tsx 的运行时 effect 注入（effect 无法写入预渲染 HTML）
 *   - og:title / twitter:title 复现 Seo.tsx 的 "| PSL Analyzer" 后缀剥离
 *   - og:image:width/height、twitter:image:alt 为跨路由图片常量，固化在 app/root.tsx，不在此处
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import type { LinkDescriptor, LinksFunction, MetaDescriptor } from 'react-router'
import { SITE, absoluteUrl, type RouteSeo } from './site'

const OG_IMAGE_ALT = `${SITE.name} — free on-device PSL facial attractiveness score`

/** 剥离 " | PSL Analyzer" 后缀，供 og:title / twitter:title 使用（与旧 Seo.tsx 一致）。 */
export function stripSiteSuffix(title: string): string {
  return title.replace(/\s*\|\s*PSL Analyzer\s*$/, '')
}

/** 路由级 meta 标签集合：title / description / robots / og:* / twitter:*。 */
export function buildMeta(seo: RouteSeo): MetaDescriptor[] {
  const url = absoluteUrl(seo.path)
  const ogTitle = stripSiteSuffix(seo.title)
  const image = absoluteUrl(SITE.ogImagePath)
  return [
    { title: seo.title },
    { name: 'description', content: seo.description },
    {
      name: 'robots',
      content: seo.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large',
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE.name },
    { property: 'og:locale', content: SITE.locale },
    { property: 'og:url', content: url },
    { property: 'og:title', content: ogTitle },
    { property: 'og:description', content: seo.description },
    { property: 'og:image', content: image },
    { property: 'og:image:alt', content: OG_IMAGE_ALT },
    { name: 'twitter:card', content: SITE.twitterCard },
    { name: 'twitter:title', content: ogTitle },
    { name: 'twitter:description', content: seo.description },
    { name: 'twitter:image', content: image },
  ]
}

/** canonical link 标签。 */
export function buildLinks(seo: RouteSeo): LinkDescriptor[] {
  return [{ rel: 'canonical', href: absoluteUrl(seo.path) }]
}

/** 便捷组合：route 文件常用 `export const links: LinksFunction = () => buildLinks(ROUTE_SEO.x)`。 */
export function linksFor(seo: RouteSeo): LinksFunction {
  return () => buildLinks(seo)
}